import { Input, Label } from '@bpinternal/ui-kit-next'
import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MinimalTiptapEditor, MinimalTiptapEditorHandle } from '~/components/Tiptap/minimal-tiptap'
import { AsyncButton } from '~/elements'
import { AddKnowledgeSourceFooter } from '~/features/knowledge-bases/components/knowledge-source/AddKnowledgeSource/AddKnowledgeSourceFooter'
import { useUploadRichTextSource } from '~/features/knowledge-bases/hooks/useKnowledgeSource'
import { KnowledgeSourceStepProps } from '~/features/knowledge-bases/types'

type UploadRichTextStepProps = {
  workspaceId: string
  botId: string
  kbId: string
  footerRef: React.RefObject<HTMLDivElement>
} & KnowledgeSourceStepProps

export const UploadRichTextStep = ({ close, botId, workspaceId, kbId, footerRef }: UploadRichTextStepProps) => {
  const [title, setTitle] = useState('')
  const editorRef = useRef<MinimalTiptapEditorHandle>(null)

  const [content, setContent] = useState<string>('')
  const { mutate: uploadRichTextSource, isPending } = useUploadRichTextSource()

  const onConfirm = () => {
    uploadRichTextSource({
      workspaceId,
      botId,
      kbId,
      content,
      title: title,
      onSuccess: () => {
        close()
      },
    })
  }

  return (
    <>
      <div className="mb-6 flex items-center gap-4 p-0.5 font-medium">
        <Label className="w-fit text-nowrap">Title</Label>
        <Input
          value={title}
          onChange={(e) => {
            setTitle(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              editorRef.current?.setFocus()
            }
          }}
          placeholder="Rich Text Source"
          className="h-auto py-1"
        />
      </div>
      <MinimalTiptapEditor
        ref={editorRef}
        content={content}
        onChange={(richText) => setContent(String(richText))}
        editorContentClassName="overflow-y-auto p-2"
        className="max-h-[calc(100vh-300px)]"
      />
      <FooterPortal footerRef={footerRef} close={close} onConfirm={onConfirm} isPending={isPending} content={content} />
    </>
  )
}

const FooterPortal = ({
  footerRef,
  close,
  onConfirm,
  isPending,
  content,
}: {
  footerRef: React.RefObject<HTMLDivElement>
  close: () => void
  onConfirm: () => void
  isPending: boolean
  content: string
}) => {
  if (!footerRef.current) return null

  return createPortal(
    <AddKnowledgeSourceFooter
      linkName="Learn more about rich text sources"
      linkHref="https://botpress.com/docs/learn/reference/knowledge-base/add-sources#rich-text"
      showCancel={true}
      onClose={close}
      primaryAction={
        <AsyncButton
          size={'sm'}
          className="min-w-[100px]"
          loading={isPending}
          disabled={content.length === 0 || isPending}
          onClick={onConfirm}
        >
          Add Rich Text
        </AsyncButton>
      }
    />,
    footerRef.current
  )
}
